// The home page's first screen: the hive. A honeycomb fills the whole section and the
// app's bees fly from the queen in the headline to task cells, work, and end done or
// waiting for your OK (landing/HiveField.jsx); the copy floats over it. Words are in
// landing.json hero.*, the jobs and their bees in landing/heroJobs.js.
import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import Bee from './bees/Bee';
import { HONEY } from './bees/beeSvg';
import HiveField from './landing/HiveField';
import { HERO_JOBS } from './landing/heroJobs';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Where "Watch a bee work" goes: the section that shows a bot taking a task (BotSteps).
const HOW_IT_WORKS = 'how-it-works';

// Wide letters (Chinese, Japanese, Korean) count more when the headline's length picks its size.
const WIDE = /[\u1100-\u11ff\u2e80-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uffef]/;
const visualLength = (text) => Array.from(text).reduce((n, ch) => n + (WIDE.test(ch) ? 1.8 : 1), 0);

/**
 * The headline with the queen after it. Its last word (or, where words have no spaces,
 * its last letter) stays with her, so she never ends up alone on a line.
 */
const Headline = ({ text, queenRef }) => {
  const cut = text.lastIndexOf(' ');
  const letters = Array.from(text);
  const head = cut >= 0 ? text.slice(0, cut + 1) : letters.slice(0, -1).join('');
  const tail = cut >= 0 ? text.slice(cut + 1) : letters.slice(-1).join('');
  return (
    <>
      {head}
      <span className="whitespace-nowrap">
        {tail}
        <span ref={queenRef} className="hive-queen" aria-hidden="true">
          <Bee species="kralovna" color={HONEY} size={96} />
        </span>
      </span>
    </>
  );
};

const Hero = () => {
  const { t } = useTranslation('landing');
  const copyRef = useRef(null);
  const queenRef = useRef(null);
  const [counts, setCounts] = useState({ work: 0, done: 0, needs: 0 });
  const touch = useMediaQuery('(hover: none)', false);
  const still = useMediaQuery('(prefers-reduced-motion: reduce)', false);
  const jobs = useMemo(() => HERO_JOBS.map((job) => ({
    ...job,
    doing: t(`hero.jobs.${job.key}.doing`),
    result: t(`hero.jobs.${job.key}.result`),
  })), [t]);
  const headline = t('hero.headline');

  const watch = (e) => {
    const target = document.getElementById(HOW_IT_WORKS);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  };
  const count = (key, n, className) => (
    <span className={className}>
      <Trans t={t} ns="landing" i18nKey={`hero.${key}`} count={n} components={{ b: <b /> }} />
    </span>
  );

  return (
    <section className="relative bg-[#0a0a0b]" aria-labelledby="hero-title">
      <HiveField
        className="lg:min-h-[760px]"
        jobs={jobs}
        copyRef={copyRef}
        homeRef={queenRef}
        onCounts={setCounts}
        approveLabel={t('hero.approve')}
        approvedLabel={t('hero.approved')}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div
            ref={copyRef}
            className="hive-hero-copy relative flex flex-col items-start gap-[22px] lg:gap-[26px] pt-[70px] lg:pt-[120px] lg:pb-20 max-w-[640px] lg:max-w-[560px] xl:max-w-[640px]"
          >
            <p className="text-[13px] font-medium tracking-[0.02em] text-honey">{t('hero.kicker')}</p>
            <h1 id="hero-title" className={`hive-hero-title${visualLength(headline) > 20 ? ' is-long' : ''}`}>
              <Headline text={headline} queenRef={queenRef} />
            </h1>
            <p className="text-base sm:text-lg leading-[1.55] text-[#c9c9d1] max-w-[56ch] text-pretty">{t('hero.subtitle')}</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="group inline-flex items-center gap-2.5 h-12 sm:h-[52px] px-5 sm:px-6 rounded-[14px] bg-honey text-honey-ink text-[15px] sm:text-base font-semibold shadow-[0_10px_30px_-10px_rgba(245,184,61,0.55)] hover:-translate-y-px hover:shadow-[0_16px_36px_-12px_rgba(245,184,61,0.7)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-honey"
              >
                {t('hero.cta_start')}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" aria-hidden="true" />
              </Link>
              <a
                href={`#${HOW_IT_WORKS}`}
                onClick={watch}
                className="inline-flex items-center h-12 sm:h-[52px] px-[18px] sm:px-[22px] rounded-[14px] border border-[#3a3a42] bg-white/[0.02] text-[#ededf0] text-[15px] sm:text-base font-medium hover:bg-white/[0.06] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-honey"
              >
                {t('hero.cta_how')}
              </a>
            </div>
            <p className="hive-status">
              {count('status_working', counts.work)}
              {count('status_done', counts.done)}
              {count('status_needs', counts.needs, 'is-needs')}
            </p>
            <p className="text-[12.5px] leading-relaxed text-[#7c7c87] max-w-[56ch]">{t(touch ? 'hero.hint_touch' : 'hero.hint')}</p>
          </div>
          {/* Phones and tablets: the bees work in the cells under the copy. */}
          <div className="h-[300px] md:h-[340px] lg:hidden" />
        </div>
      </HiveField>
    </section>
  );
};

export default Hero;
